import { motion } from 'framer-motion';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT, useLang } from '@/lib/i18n';
import { 
  Briefcase, Users, Heart, GraduationCap,
  Clock, MapPin, ArrowRight, CheckCircle, AlertCircle,
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { CTASection } from '@/components/sections/CTASection';
import { getBreadcrumbJsonLd, getFaqJsonLd, BASE_URL } from '@/lib/seo-constants';

export default function KarrierePage() {
  const t = useT();
  const lang = useLang();
  const benefits = [
    { icon: Briefcase, title: t('Modernes Arbeitsumfeld', 'Modern workplace'), description: t('Sauberes und technisch bestens ausgestattetes Fertigungsumfeld mit neuesten CNC-Maschinen.', 'Clean, well-equipped production environment with the latest CNC machines.') },
    { icon: Heart, title: t('Leistungsgerechte Vergütung', 'Performance-based pay'), description: t('Faire Bezahlung mit attraktiven Zusatzleistungen, weil wir wissen, was deine Arbeit wert ist.', 'Fair pay with attractive benefits — because we know what your work is worth.') },
    { icon: GraduationCap, title: t('Weiterentwicklung', 'Personal development'), description: t('Schulungen, Maschinenbedienung und CAD/CAM-Kenntnisse und vielfältige Entwicklungsmöglichkeiten.', 'Training, machine operation, CAD/CAM skills and varied development opportunities.') },
    { icon: Users, title: t('Kollegiales Team', 'Collegial team'), description: t('Ein offenes Team, das gemeinsam an einem Strang zieht und Erfolge feiert.', 'An open team that pulls together and celebrates success.') },
  ];
  const values = [
    t('CNC-Drehen und Fräsen', 'CNC turning and milling'),
    t('Moderne HURCO & Emco Maschinen', 'Modern HURCO & Emco machines'),
    t('CAD/CAM mit SolidWorks', 'CAD/CAM with SolidWorks'),
    t('Flexibles Arbeiten', 'Flexible work'),
    t('Nachhaltige Unternehmenskultur', 'Sustainable company culture'),
    t('Regionale Verwurzelung', 'Regional roots'),
  ];
  return (
    <Layout>
      <SEOHead
        title={t('Karriere – Jobs in der CNC-Fertigung', 'Careers – Jobs in CNC manufacturing')}
        description={t('Karriere bei FERNAU Präzisionstechnik in Darmstadt. Offene Stellen in CNC-Drehen, Fräsen, CAD/CAM. Modernes Arbeitsumfeld, faire Vergütung.', 'Careers at FERNAU Präzisionstechnik in Darmstadt, Germany. Open positions in CNC turning, milling and CAD/CAM. Modern workplace, fair pay.')}
        path="/karriere"
        jsonLd={[
          getBreadcrumbJsonLd([{ name: t('Karriere', 'Careers'), path: '/karriere' }], lang),
          {
            '@context': 'https://schema.org',
            '@type': 'JobPosting',
            title: t('Initiativbewerbung – CNC-Zerspanung', 'Unsolicited application – CNC machining'),
            description: t(
              'FERNAU Präzisionstechnik sucht erfahrene Fachkräfte für CNC-Drehen und Fräsen. Modernes Arbeitsumfeld, faire Vergütung, Standort Darmstadt.',
              'FERNAU Präzisionstechnik is hiring experienced specialists for CNC turning and milling. Modern workplace, fair pay, located in Darmstadt, Germany.',
            ),
            datePosted: '2026-03-01',
            hiringOrganization: { '@id': `${BASE_URL}/#organization` },
            jobLocation: {
              '@type': 'Place',
              address: { '@type': 'PostalAddress', addressLocality: 'Darmstadt', addressRegion: 'Hessen', addressCountry: 'DE' },
            },
            employmentType: 'FULL_TIME',
            inLanguage: lang,
          },
          getFaqJsonLd(
            lang === 'en'
              ? [
                  { question: 'Are there open positions at FERNAU?', answer: 'No positions are currently advertised, but unsolicited applications are always welcome. We are looking for specialists in CNC turning, milling and CAD/CAM.' },
                  { question: 'Does FERNAU offer internships?', answer: 'Yes, we generally offer school internships (1 to 3 weeks) and mandatory internships for students of technical and commercial subjects. Please note: we are currently unable to accept interns for 2027.' },
                ]
              : [
                  { question: 'Gibt es offene Stellen bei FERNAU?', answer: 'Aktuell sind keine Stellen ausgeschrieben, jedoch sind Initiativbewerbungen jederzeit willkommen. Wir suchen Fachkräfte für CNC-Drehen, Fräsen und CAD/CAM.' },
                  { question: 'Bietet FERNAU Praktika an?', answer: 'Ja, wir bieten grundsätzlich Schülerpraktika (1–3 Wochen) und Pflichtpraktika für Studierende technischer und kaufmännischer Fachrichtungen an. Hinweis: Aktuell können wir für 2027 keine Praktikanten annehmen.' },
                ],
            lang,
          ),
        ]}
      />
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.span
            className="text-primary font-mono text-sm uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {t('Karriere bei FERNAU', 'Careers at FERNAU')}
          </motion.span>
          <motion.h1
            className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {t(<>Werde Teil unseres <span className="text-primary">Teams</span></>, <>Become part of our <span className="text-primary">team</span></>)}
          </motion.h1>
          <motion.p
            className="mt-6 text-xl text-muted-foreground max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {t(
              'Bei uns zählst DU als Mensch, nicht als Personalnummer. Entdecke ein Arbeitsumfeld, das Präzision und Menschlichkeit verbindet.',
              'Here YOU count as a person, not a payroll number. Discover a workplace that combines precision with humanity.'
            )}
          </motion.p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-center mb-12">{t('Warum FERNAU?', 'Why FERNAU?')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                className="card-industrial p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
                  <benefit.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{benefit.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Openings */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold">{t('Aktuelle Stellenangebote', 'Current openings')}</h2>
            
            <motion.div
              className="mt-8 p-6 sm:p-8 rounded-xl bg-secondary/50 border border-border/50"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Briefcase className="w-8 h-8 text-primary" />
              </div>
              <h3 className="mt-4 text-xl font-semibold">{t('Initiativbewerbung willkommen', 'Speculative applications welcome')}</h3>
              <p className="mt-3 text-muted-foreground">
                {t(
                  'Derzeit sind keine offenen Stellen ausgeschrieben. Dennoch sind wir immer offen für den Austausch mit spannenden Persönlichkeiten. Wenn Du Erfahrung in der CNC-Bearbeitung, Drehen oder Fräsen mitbringst und Lust hast, in einem modernen Fertigungsumfeld mitzuwirken, freuen wir uns auf Deine Initiativbewerbung.',
                  "There are currently no open positions advertised. Still, we are always open to talking with interesting people. If you have experience in CNC machining, turning or milling and want to contribute in a modern manufacturing environment, we look forward to your speculative application.",
                )}
              </p>
              
              <div className="mt-6 flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4 text-primary" />
                  Darmstadt
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4 text-primary" />
                  {t('Vollzeit', 'Full-time')}
                </div>
              </div>

              <Button variant="hero" size="lg" className="mt-8" asChild>
                <LocalizedLink to="/kontakt">
                  {t('Initiativbewerbung senden', 'Send a speculative application')}
                  <ArrowRight className="w-4 h-4" />
                </LocalizedLink>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Work With */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold">{t('Womit wir arbeiten', 'What we work with')}</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                {t(
                  'Als Mitarbeiter bei FERNAU arbeitest du mit modernsten CNC-Maschinen und Technologien. Wir investieren kontinuierlich in unseren Maschinenpark und die Weiterbildung unseres Teams.',
                  'As a FERNAU team member you work with state-of-the-art CNC machines and technologies. We continuously invest in our machine park and in the development of our team.',
                )}
              </p>
              
              <div className="mt-8 grid grid-cols-2 gap-4">
                {values.map((value) => (
                  <div key={value} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                    <span className="text-sm">{value}</span>
                  </div>
                ))}
              </div>

              <Button variant="steel" size="lg" className="mt-8" asChild>
                <LocalizedLink to="/maschinenpark">
                  {t('Maschinenpark ansehen', 'View machine park')}
                  <ArrowRight className="w-4 h-4" />
                </LocalizedLink>
              </Button>
            </div>

            <motion.div
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {[
                { value: '11', label: t('Maschinen', 'Machines') },
                { value: '1995', label: t('Gegründet', 'Founded') },
                { value: 'ISO', label: t('9001 Zertifiziert', '9001 certified') },
                { value: '100%', label: t('Ökostrom', 'Green power') },
              ].map((stat) => (
                <div key={stat.label} className="stat-card text-center">
                  <div className="text-3xl font-bold font-mono text-primary">{stat.value}</div>
                  <div className="mt-2 text-sm text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Praktika */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold">
                {t('Praktikum bei ', 'Internship at ')}<span className="text-primary">FERNAU</span>
              </h2>
              <div
                role="note"
                className="mt-8 rounded-xl border-2 border-primary/50 bg-primary/10 px-5 py-6"
              >
                <AlertCircle className="w-8 h-8 text-primary mx-auto" aria-hidden="true" />
                <h3 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
                  {t(
                    'Aktuell können wir für 2027 keine Praktikanten annehmen.',
                    'We are currently unable to accept interns for 2027.',
                  )}
                </h3>
              </div>

              <p className="mt-6 text-muted-foreground">
                {t(
                  'Du möchtest die Welt der CNC-Zerspanung hautnah erleben? Wir bieten grundsätzlich Praktikumsplätze für Schüler und Studierende.',
                  'Want to experience the world of CNC machining first-hand? We generally offer internships for pupils and students.',
                )}
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-6">
              <motion.div
                className="p-6 rounded-xl bg-background border border-border/40 hover:border-primary/30 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <GraduationCap className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">{t('Schülerpraktikum', 'Pupil internship')}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {t(
                    'Ideal für Schüler ab der 8. Klasse, die einen Einblick in die metallverarbeitende Industrie und moderne CNC-Fertigung erhalten möchten. Du lernst unsere Maschinen kennen, begleitest erfahrene Fachkräfte und fertigst dein erstes eigenes Werkstück.',
                    'Ideal for pupils from year 8 who want to look into the metalworking industry and modern CNC manufacturing. You get to know our machines, shadow experienced staff and produce your first own part.',
                  )}
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                    {t('Dauer: 1–3 Wochen', 'Duration: 1–3 weeks')}
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                    {t('Einblick in Drehen, Fräsen & Montage', 'Insight into turning, milling & assembly')}
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                    {t('Persönliche Betreuung', 'Personal mentoring')}
                  </li>
                </ul>
              </motion.div>

              <motion.div
                className="p-6 rounded-xl bg-background border border-border/40 hover:border-primary/30 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Briefcase className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">{t('Pflichtpraktikum für Studierende', 'Mandatory internship for students')}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {t(
                    'Für Studierende technischer oder kaufmännischer Fachrichtungen (Maschinenbau, Wirtschaft, Büromanagement o.ä.), die ihr Pflichtpraktikum in einem praxisnahen Umfeld absolvieren möchten. Du wirst aktiv in laufende Projekte eingebunden.',
                    'For students of technical or business disciplines (mechanical engineering, business, office management or similar) who want to complete their mandatory internship in a hands-on environment. You will be actively involved in ongoing projects.',
                  )}
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                    {t('Dauer nach Studienordnung', 'Duration per study regulations')}
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                    {t('Fertigung, Montage oder Verwaltung', 'Production, assembly or administration')}
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                    {t('Eigenverantwortliche Projektarbeit', 'Independent project work')}
                  </li>
                </ul>
              </motion.div>
            </div>

            <motion.div
              className="mt-8 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Button variant="hero" size="lg" asChild>
                <LocalizedLink to="/kontakt">
                  {t('Praktikum anfragen', 'Request an internship')}
                  <ArrowRight className="w-4 h-4" />
                </LocalizedLink>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
    </Layout>
  );
}
